/* mo:mo PDP-Main — Varianten-Picker, Galerie, Menge. */
class MomoPdp extends HTMLElement {
  connectedCallback() {
    try { this.variants = JSON.parse(this.querySelector('[data-product-json]').textContent); }
    catch (e) { this.variants = []; }
    this.idInput = this.querySelector('[data-variant-id]');
    this.priceEl = this.querySelector('[data-price]');
    this.atc = this.querySelector('[data-atc]');
    this.shipEl = this.querySelector('[data-shipping-note]');
    this.colorValueEl = this.querySelector('[data-color-value]');
    this.stage = this.querySelector('[data-pdp-stage]');

    this.addEventListener('change', (e) => { if (e.target.matches('[data-option]')) this.onVariantChange(); });
    this.bindGallery();
    this.bindQty();
    /* Beim ersten Laden dieselbe Logik wie bei jedem Wechsel: sonst überschreibt der
       Farbfilter das von Liquid gesetzte Variantenbild mit dem ersten Thumbnail. */
    this.onVariantChange();
  }

  money(cents) {
    return (cents / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  }

  selectedOptions() {
    return [...this.querySelectorAll('[data-option]:checked')]
      .sort((a, b) => a.dataset.option - b.dataset.option)
      .map((i) => i.value);
  }

  findVariant(opts) {
    return this.variants.find((v) => v.options.length === opts.length && v.options.every((o, i) => o === opts[i]));
  }

  /* Positionen der im DOM gerenderten Optionen, aufsteigend. */
  optionPositions() {
    if (!this._positions) {
      const seen = new Set();
      this.querySelectorAll('[data-option]').forEach((i) => seen.add(Number(i.dataset.option)));
      this._positions = [...seen].sort((a, b) => a - b);
    }
    return this._positions;
  }

  inputsAt(position) {
    return [...this.querySelectorAll(`[data-option="${position}"]`)];
  }

  /* Die Verfügbarkeitslogik braucht den vollständigen Optionsvektor. Werden Optionen als
     verknüpfte Produkte gerendert (Farbe über custom.farbvarianten), fehlen Positionen im
     DOM — dann bleibt alles beim bisherigen Verhalten statt falsch zu sperren. */
  coversAllOptions() {
    const total = Number(this.dataset.optionCount || 0);
    const pos = this.optionPositions();
    return total > 0 && pos.length === total && pos.every((p, i) => p === i + 1);
  }

  /* Shopifys Regel für product_option_value.available, clientseitig nachgebildet:
     Ein Wert an Position N wird gegen die bereits gewählten Werte der Positionen 1…N−1
     bewertet und schaut nach vorn auf N+1…Ende — spätere Optionen bleiben unbeschränkt.
     Dadurch entstehen keine Sackgassen: Eine frühere Option wird nie gesperrt, solange
     irgendeine spätere Kombination kaufbar ist. */
  hasPurchasable(prefix, position, value) {
    return this.variants.some((v) => {
      if (!v.available) return false;
      for (let i = 0; i < prefix.length; i++) {
        if (prefix[i] !== null && v.options[i] !== prefix[i]) return false;
      }
      return v.options[position - 1] === value;
    });
  }

  /* Zeigt die Auswahl auf eine nicht mehr mögliche Kombination, rutscht sie auf den ersten
     gültigen Wert — von vorn nach hinten, damit die gerade getroffene Wahl stehen bleibt
     und nur die nachgelagerten Optionen nachziehen. */
  reconcileSelection() {
    const prefix = [];
    this.optionPositions().forEach((position) => {
      const inputs = this.inputsAt(position);
      let current = inputs.find((i) => i.checked);
      if (!current || !this.hasPurchasable(prefix, position, current.value)) {
        const fallback = inputs.find((i) => this.hasPurchasable(prefix, position, i.value));
        if (fallback) {
          inputs.forEach((i) => { i.checked = false; });
          fallback.disabled = false;
          fallback.checked = true;
          current = fallback;
        }
      }
      prefix.push(current ? current.value : null);
    });
  }

  /* Nicht erreichbare Werte ausgrauen und aus der Bedienung nehmen. */
  markAvailability() {
    const prefix = [];
    this.optionPositions().forEach((position) => {
      const inputs = this.inputsAt(position);
      inputs.forEach((input) => {
        const ok = this.hasPurchasable(prefix, position, input.value);
        input.disabled = !ok;
        const label = input.id ? this.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null;
        if (!label) return;
        label.classList.toggle('is-unavailable', !ok);
        if (ok) label.removeAttribute('aria-disabled');
        else label.setAttribute('aria-disabled', 'true');
      });
      const checked = inputs.find((i) => i.checked);
      prefix.push(checked ? checked.value : null);
    });
  }

  onVariantChange() {
    if (this.coversAllOptions()) {
      this.reconcileSelection();
      this.markAvailability();
    }
    const opts = this.selectedOptions();
    const variant = this.findVariant(opts);
    let stageFromVariant = false;

    if (this.colorValueEl) {
      const c = this.querySelector('[data-color]:checked');
      if (c) this.colorValueEl.textContent = c.value;
    }

    if (variant) {
      if (this.idInput) this.idInput.value = variant.id;
      if (this.priceEl) this.priceEl.textContent = this.money(variant.price);
      if (this.atc) {
        this.atc.disabled = !variant.available;
        this.atc.textContent = variant.available ? this.atc.dataset.label : (this.atc.dataset.soldout || 'Ausverkauft');
      }
      /* Digitale Varianten (Voucher, Sofort-Start) sind nicht versandpflichtig —
         dann darf kein Versandhinweis stehen. */
      if (this.shipEl) this.shipEl.hidden = !variant.requires_shipping;
      if (variant.featured_media && this.stage) {
        const src = variant.featured_media.preview_image && variant.featured_media.preview_image.src;
        if (src) {
          this.setStage(src.split('?')[0] + '?width=1200', variant.featured_media.alt || undefined, variant.featured_media.id);
          stageFromVariant = true;
        }
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    } else if (this.atc) {
      this.atc.disabled = true;
      this.atc.textContent = 'Nicht verfügbar';
    }
    this.updateBundlePrices();
    /* Variantenbild hat Vorrang: sonst überschreibt der Farbfilter das gerade gesetzte
       Hauptbild mit dem ersten sichtbaren Thumbnail — und Variantenbilder stehen dort
       gar nicht drin, weil sie zur Vermeidung von Doppelungen ausgeblendet werden. */
    this.filterGalleryByColor(stageFromVariant);
  }

  /* Farb-Galerie: zeigt nur Thumbnails der gewählten Farbe (data-thumb-color) + gemeinsame (ohne Farbe).
     Ohne Farb-Tags (kein Alt-Präfix gepflegt) bleibt die Galerie unangetastet — dann greift nur der native
     Hauptbild-Wechsel via variant.featured_media aus onVariantChange. */
  filterGalleryByColor(preserveStage) {
    const colorInput = this.querySelector('[data-color]:checked');
    if (!colorInput) return;
    const color = colorInput.value.toLowerCase().trim();
    const thumbs = [...this.querySelectorAll('[data-pdp-thumb]')];
    if (!thumbs.length) return;
    const anyColored = thumbs.some((t) => (t.dataset.thumbColor || '') !== '');
    if (!anyColored) return;

    let firstVisible = null;
    let firstColor = null;
    thumbs.forEach((t) => {
      const tc = (t.dataset.thumbColor || '').toLowerCase();
      const show = tc === '' || tc === color;
      t.hidden = !show;
      if (show && !firstVisible) firstVisible = t;
      if (tc === color && !firstColor) firstColor = t;
    });

    thumbs.forEach((t) => t.classList.remove('is-active'));
    /* Hat die Variante ein eigenes Bild geliefert, bleibt es stehen — dann ist auch kein
       Thumbnail aktiv, weil das Variantenbild bewusst nicht im Streifen auftaucht. */
    if (preserveStage) return;
    const lead = firstColor || firstVisible;
    if (lead) {
      lead.classList.add('is-active');
      if (lead.dataset.full) this.setStage(lead.dataset.full, lead.dataset.alt, lead.dataset.mediaId);
    }
  }

  /* Voll-Preis je Bundle-Karte: günstigste passende Variante (bei gewählter Farbe). */
  updateBundlePrices() {
    const colorInput = this.querySelector('[data-color]:checked');
    const colorPos = colorInput ? colorInput.dataset.option : null;
    const colorVal = colorInput ? colorInput.value : null;

    this.querySelectorAll('.momo-pdp__bundle').forEach((label) => {
      const bundleVal = label.dataset.bundle;
      const priceEl = label.querySelector('[data-bundle-price]');
      if (!priceEl) return;
      const match = this.variants.filter((v) => {
        const okBundle = v.options.includes(bundleVal);
        const okColor = colorVal ? v.options[colorPos - 1] === colorVal : true;
        return okBundle && okColor;
      });
      if (!match.length) return;
      /* MOMO-7 · Streichpreis und Badge stammen aus DERSELBEN Variante wie der Preis. Vorher nahm
         Liquid die erste passende Variante und das JS das Minimum — zwei Quellen fuer eine Kachel. */
      const variant = match.reduce((a, b) => (b.price < a.price ? b : a));
      priceEl.textContent = this.money(variant.price);
      this.updateBundleSale(label, variant);
    });
  }

  /* Streichpreis und Badge einer Kachel nachziehen. Beide Elemente existieren nur, wenn der
     jeweilige Schalter in der Section an ist — fehlt eines, bleibt es hier unberuehrt. Ohne
     Rabatt werden sie versteckt statt geleert, damit ein spaeterer Farbwechsel sie wieder
     einblenden kann. */
  updateBundleSale(label, variant) {
    const wasEl = label.querySelector('[data-bundle-was]');
    const badgeEl = label.querySelector('[data-bundle-badge]');
    if (!wasEl && !badgeEl) return;
    const compare = variant.compare_at_price || 0;
    const onSale = compare > variant.price;
    const saved = onSale ? compare - variant.price : 0;

    if (wasEl) {
      wasEl.textContent = onSale ? this.money(compare) : '';
      wasEl.hidden = !onSale;
    }
    if (badgeEl) {
      badgeEl.textContent = saved > 0 ? '−' + this.money(saved).replace(' ', ' ') : '';
      badgeEl.hidden = saved === 0;
    }
    /* Traegt die Mobil-Regel in momo-components.css: nur wenn wirklich beides sichtbar ist. */
    const both = !!(wasEl && !wasEl.hidden && badgeEl && !badgeEl.hidden);
    label.classList.toggle('momo-pdp__bundle--both', both);
  }

  /* alt optional: wandert beim Bildwechsel mit, damit das Hauptbild nicht den Alt-Text
     des zuerst geladenen Bildes behält. Ohne Angabe bleibt der bisherige stehen.
     MOMO-14 · mediaId optional: Steht dasselbe Bild schon in der Bühne, bleibt es
     unangetastet. Sonst würde der erste Durchlauf dieselbe Aufnahme unter leicht anderer
     URL (Liquid setzt ?v=…&width=, das JS nur ?width=) erneut laden — sichtbar als Blitzer. */
  setStage(src, alt, mediaId) {
    if (!this.stage) return;
    if (mediaId && String(mediaId) === this.stage.dataset.mediaId) return;
    this.stage.src = src;
    if (typeof alt === 'string') this.stage.alt = alt;
    if (mediaId) this.stage.dataset.mediaId = String(mediaId);
    else delete this.stage.dataset.mediaId;
  }

  bindGallery() {
    this.querySelectorAll('[data-pdp-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        this.querySelectorAll('[data-pdp-thumb]').forEach((t) => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
        if (thumb.dataset.full) this.setStage(thumb.dataset.full, thumb.dataset.alt, thumb.dataset.mediaId);
      });
    });
  }

  bindQty() {
    const input = this.querySelector('[data-qty]');
    if (!input) return;
    const step = (d) => {
      const v = Math.max(1, (parseInt(input.value, 10) || 1) + d);
      input.value = v;
    };
    const minus = this.querySelector('[data-qty-minus]');
    const plus = this.querySelector('[data-qty-plus]');
    if (minus) minus.addEventListener('click', () => step(-1));
    if (plus) plus.addEventListener('click', () => step(1));
  }
}
customElements.define('momo-pdp', MomoPdp);
